import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from 'app/services/api.service';
import {
    GlobalSearchData,
    GlobalSearchResponse,
    GlobalSearchResultItem,
    SearchResultItemUI,
    SearchResultSet
} from './search.types';

export const SEARCH_CATEGORY_CONFIG: Record<
    string,
    { label: string; icon: string; bgClass: string; badgeColorClass: string; linkPrefix: string }
> = {
    customers: {
        label: 'Customers',
        icon: 'heroicons_solid:user',
        bgClass: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
        badgeColorClass: 'text-blue-600 dark:text-blue-400 bg-blue-500/10 border-blue-500/25',
        linkPrefix: '/trip-mgmt/customers'
    },
    drivers: {
        label: 'Drivers',
        icon: 'heroicons_solid:truck',
        bgClass: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
        badgeColorClass: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/25',
        linkPrefix: '/driver-mgmt/drivers'
    },
    trips: {
        label: 'Trips',
        icon: 'heroicons_solid:map-pin',
        bgClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
        badgeColorClass: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/25',
        linkPrefix: '/trip-mgmt/trips'
    },
    wallets: {
        label: 'Wallets',
        icon: 'heroicons_solid:wallet',
        bgClass: 'bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-400',
        badgeColorClass: 'text-purple-600 dark:text-purple-400 bg-purple-500/10 border-purple-500/25',
        linkPrefix: '/admin/wallet-management/wallet-details'
    },
    leads: {
        label: 'Leads',
        icon: 'heroicons_solid:funnel',
        bgClass: 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400',
        badgeColorClass: 'text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/25',
        linkPrefix: '/crm/leads'
    }
};

export const SEARCH_TYPE_CONFIG: Record<
    string,
    { label: string; icon: string; bgClass: string; badgeColorClass: string; linkPrefix: string }
> = {
    CUSTOMER: {
        label: 'Customers',
        icon: 'heroicons_solid:user',
        bgClass: 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
        badgeColorClass: 'text-blue-600 dark:text-blue-400 bg-blue-500/10 border-blue-500/25',
        linkPrefix: '/trip-mgmt/customers'
    },
    DRIVER: {
        label: 'Drivers',
        icon: 'heroicons_solid:truck',
        bgClass: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
        badgeColorClass: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/25',
        linkPrefix: '/driver-mgmt/drivers'
    },
    TRIP: {
        label: 'Trips',
        icon: 'heroicons_solid:map-pin',
        bgClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
        badgeColorClass: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/25',
        linkPrefix: '/trip-mgmt/trips'
    },
    WALLET: {
        label: 'Wallets',
        icon: 'heroicons_solid:wallet',
        bgClass: 'bg-purple-50 text-purple-600 dark:bg-purple-500/15 dark:text-purple-400',
        badgeColorClass: 'text-purple-600 dark:text-purple-400 bg-purple-500/10 border-purple-500/25',
        linkPrefix: '/admin/wallet-management/wallet-details'
    },
    LEAD: {
        label: 'Leads',
        icon: 'heroicons_solid:funnel',
        bgClass: 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400',
        badgeColorClass: 'text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/25',
        linkPrefix: '/crm/leads'
    }
};

@Injectable({ providedIn: 'root' })
export class SearchService {
    /**
     * Constructor
     */
    constructor(private _apiService: ApiService) {}

    /**
     * Execute global search
     * GET /admin/api/v1/global-search?q={query}&limit={limit}
     *
     * @param query
     * @param limit
     */
    search(query: string, limit: number = 5): Observable<GlobalSearchResponse> {
        return this._apiService.sendGETRequest('/v1/global-search', {
            q: query,
            limit
        });
    }

    /**
     * Map raw API response data to categorized ResultSets for UI
     *
     * @param data
     */
    mapToResultSets(data: GlobalSearchData | any): SearchResultSet[] {
        if (!data) {
            return [];
        }

        const results = data.results || (data.data && data.data.results);
        if (!results) {
            return [];
        }

        const categoryOrder = ['customers', 'drivers', 'trips', 'wallets', 'leads'];
        const allKeys = Object.keys(results);

        // Sort keys by priority order with unknown categories appended
        const sortedKeys = allKeys.sort((a, b) => {
            const indexA = categoryOrder.indexOf(a.toLowerCase());
            const indexB = categoryOrder.indexOf(b.toLowerCase());
            const scoreA = indexA === -1 ? 999 : indexA;
            const scoreB = indexB === -1 ? 999 : indexB;
            return scoreA - scoreB;
        });

        const resultSets: SearchResultSet[] = [];

        for (const key of sortedKeys) {
            const items = results[key];
            if (!Array.isArray(items) || items.length === 0) {
                continue;
            }

            const categoryConfig =
                SEARCH_CATEGORY_CONFIG[key.toLowerCase()] ||
                (items[0]?.type ? SEARCH_TYPE_CONFIG[items[0].type.toUpperCase()] : null) || {
                    label: key.charAt(0).toUpperCase() + key.slice(1),
                    icon: 'heroicons_solid:magnifying-glass',
                    bgClass: 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                    badgeColorClass: 'text-slate-600 dark:text-slate-400 bg-slate-500/10 border-slate-500/20',
                    linkPrefix: '/'
                };

            const mappedResults: SearchResultItemUI[] = items.map((item: GlobalSearchResultItem) => {
                const itemTypeConfig = item.type ? SEARCH_TYPE_CONFIG[item.type.toUpperCase()] : null;
                const linkPrefix = itemTypeConfig?.linkPrefix || categoryConfig.linkPrefix;

                const isPhoneSubtitle = !!(item.subtitle && /^\+?[\d\s\-()]{7,}$/.test(item.subtitle.trim()));
                const isEmailDescription = !!(item.description && item.description.includes('@'));
                const isRouteDescription = !!(item.description && (item.description.includes('→') || item.description.includes('->')));

                return {
                    ...item,
                    link: [linkPrefix, item.id],
                    value: item.title,
                    statusColorClass: this.getStatusColorClass(item.status),
                    statusDotClass: this.getStatusDotClass(item.status),
                    isPhoneSubtitle,
                    isEmailDescription,
                    isRouteDescription
                };
            });

            resultSets.push({
                id: key,
                label: categoryConfig.label,
                icon: categoryConfig.icon,
                bgClass: categoryConfig.bgClass,
                badgeColorClass: categoryConfig.badgeColorClass,
                results: mappedResults
            });
        }

        return resultSets;
    }

    /**
     * Status color utility for styling status badges
     *
     * @param status
     */
    getStatusColorClass(status?: string): string {
        if (!status) return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/25';
        const s = status.toUpperCase();
        if (['ENABLED', 'APPROVED', 'ACTIVE', 'COMPLETED', 'SUCCESS', 'RESOLVED', 'VERIFIED'].includes(s)) {
            return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/25';
        }
        if (['PENDING', 'NEW', 'IN_PROGRESS', 'PROCESSING', 'SCHEDULED', 'ASSIGNED'].includes(s)) {
            return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/25';
        }
        if (['BLOCKED', 'CANCELLED', 'REJECTED', 'DISABLED', 'FAILED', 'EXPIRED'].includes(s)) {
            return 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/25';
        }
        return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/25';
    }

    /**
     * Status dot pulse color class
     *
     * @param status
     */
    getStatusDotClass(status?: string): string {
        if (!status) return 'bg-slate-400';
        const s = status.toUpperCase();
        if (['ENABLED', 'APPROVED', 'ACTIVE', 'COMPLETED', 'SUCCESS', 'RESOLVED', 'VERIFIED'].includes(s)) {
            return 'bg-emerald-500 animate-pulse';
        }
        if (['PENDING', 'NEW', 'IN_PROGRESS', 'PROCESSING', 'SCHEDULED', 'ASSIGNED'].includes(s)) {
            return 'bg-blue-500';
        }
        if (['BLOCKED', 'CANCELLED', 'REJECTED', 'DISABLED', 'FAILED', 'EXPIRED'].includes(s)) {
            return 'bg-rose-500';
        }
        return 'bg-slate-400';
    }
}
